/**
 * «Зараз»: the latest measurement — charge with the minimum-charge mark,
 * battery, grid, PV (objects with PV only; below 0,1 kW — 0) and
 * consumption, the direction in words. Without connection — the last data
 * with its time.
 */
import type { CSSProperties, ReactNode } from 'react';
import type { SiteOverviewResponse } from 'shared';

import { fmtDateTime, fmtNum, fmtTime } from '../../../components/format.js';
import type { ConnState } from '../../../components/site-state.js';
import { useLocale } from '../../../i18n/locale-context.js';
import type { MessageKey } from '../../../i18n/messages.js';

const PV_MIN_KW = 0.1;
const IDLE_KW = 0.05;

export function NowCard({
  latest,
  hasPv,
  socMinPct,
  conn,
  tz,
}: {
  latest: SiteOverviewResponse['latest'];
  hasPv: boolean;
  socMinPct: number;
  conn: ConnState;
  tz: string;
}) {
  const { t, tf, locale } = useLocale();
  const sub = latest
    ? conn === 'ok' || conn === 'noedit'
      ? tf('site.now.at', { time: fmtTime(latest.ts, tz) })
      : tf('site.now.last', { time: fmtDateTime(latest.ts, tz) })
    : null;

  const tile = (labelKey: MessageKey, v: number | null, dir: string, unit: string) => (
    <div className="tile" key={labelKey}>
      <span className="label">{t(labelKey)}</span>
      {v === null ? (
        <span className="dir">{t('site.now.no_value')}</span>
      ) : (
        <>
          <span className="value num">
            {fmtNum(locale, Math.abs(v))}
            <small>{unit}</small>
          </span>
          <span className="dir">{dir}</span>
        </>
      )}
    </div>
  );

  let tiles: ReactNode[] = [];
  if (latest) {
    const soc = latest.soc_pct;
    const bat = latest.p_battery_kw; // + charge / − discharge (vendor canon)
    const grid = latest.p_grid_kw; // + import / − export
    const pv = latest.p_pv_kw === null ? null : latest.p_pv_kw < PV_MIN_KW ? 0 : latest.p_pv_kw;
    const kw = t('site.unit.kw');
    const word = (v: number | null, pos: MessageKey, neg: MessageKey, idle: MessageKey) =>
      v === null ? '' : t(Math.abs(v) < IDLE_KW ? idle : v > 0 ? pos : neg);
    tiles = [
      <div className="tile" key="soc">
        <span className="label">{t('site.now.soc')}</span>
        {soc === null ? (
          <span className="dir">{t('site.now.no_value')}</span>
        ) : (
          <span className="value num">
            {fmtNum(locale, soc)}
            <small>%</small>
          </span>
        )}
        <div className="socbar" aria-hidden="true">
          <i style={{ width: `${Math.min(100, Math.max(0, soc ?? 0))}%` }} />
          <b style={{ left: `${socMinPct}%` }} />
        </div>
        <span className="sub">{tf('site.now.soc_min', { pct: socMinPct })}</span>
      </div>,
      tile('site.now.battery', bat, word(bat, 'site.now.charge', 'site.now.discharge', 'site.now.idle'), kw),
      tile('site.now.grid', grid, word(grid, 'site.now.buy', 'site.now.sell', 'site.now.no_exchange'), kw),
      ...(hasPv ? [tile('site.now.pv', pv, t('site.now.generation'), kw)] : []),
      tile('site.now.load', latest.p_load_kw, t('site.now.object_load'), kw),
    ];
  }

  return (
    <section className="card" aria-labelledby="now-title">
      <div className="card-head">
        <h2 id="now-title">{t('site.now.title')}</h2>
        {sub ? <span className="sub">{sub}</span> : null}
      </div>
      {latest ? (
        <div
          className={`tiles${(tiles.length - 1) % 2 === 1 ? ' odd' : ''}`}
          style={{ '--n': String(tiles.length) } as CSSProperties}
        >
          {tiles}
        </div>
      ) : (
        <p className="muted">
          {t(conn === 'pending' || conn === 'closed' ? 'site.now.no_access' : 'site.now.none')}
        </p>
      )}
    </section>
  );
}
